/**
 * Universal Data Fetching Hook
 * Works for ANY API endpoint in the application
 * Features: loading states, error handling, caching, refetch, manual updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiCache from '../utils/cache';
import { handleApiError } from '../utils/errorHandler';

/**
 * Universal API hook for data fetching
 *
 * @param {Function} apiCall - Service method to call (should return a Promise)
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to auto-fetch on mount (default: true)
 * @param {boolean} options.cache - Whether to use caching (default: true)
 * @param {number} options.cacheTTL - Cache time-to-live in milliseconds
 * @param {string} options.cacheKey - Custom cache key (auto-generated if not provided)
 * @param {Array} options.dependencies - Dependencies to trigger refetch (like useEffect deps)
 * @param {Function} options.onSuccess - Callback on successful fetch
 * @param {Function} options.onError - Callback on error
 * @param {Object} options.context - Additional context for error logging
 *
 * @returns {Object} - { data, loading, error, refetch, mutate, isFetching }
 */
const useApi = (apiCall, options = {}) => {
  const {
    enabled = true,
    cache = true,
    cacheTTL,
    cacheKey,
    dependencies = [],
    onSuccess,
    onError,
    context = {},
  } = options;

  // State
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  // Refs
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const callCountRef = useRef(0);

  // Generate cache key if not provided
  const generatedCacheKey = useCallback(() => {
    if (cacheKey) return cacheKey;

    // Generate key from function name and dependencies
    const functionName = apiCall.name || 'anonymous';
    const depsString = dependencies.length > 0 ? JSON.stringify(dependencies) : '';
    return `${functionName}-${depsString}`;
  }, [cacheKey, apiCall, dependencies]);

  /**
   * Main fetch function
   */
  const fetchData = useCallback(async (forceRefresh = false) => {
    // Increment call count for debugging
    callCountRef.current += 1;
    const currentCall = callCountRef.current;

    try {
      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      // Check cache first (if enabled and not forcing refresh)
      if (cache && !forceRefresh) {
        const cachedData = apiCache.get(generatedCacheKey());
        if (cachedData !== null) {
          if (isMountedRef.current) {
            setData(cachedData);
            setLoading(false);
            setError(null);
          }
          return cachedData;
        }
      }

      // Set loading states
      if (isMountedRef.current) {
        setIsFetching(true);
        if (!data) {
          setLoading(true);
        }
        setError(null);
      }

      // Make API call
      const result = await apiCall();

      // Only update state if this is still the latest call and component is mounted
      if (currentCall === callCountRef.current && isMountedRef.current) {
        setData(result);
        setLoading(false);
        setIsFetching(false);
        setError(null);

        // Cache the result
        if (cache) {
          apiCache.set(generatedCacheKey(), result, cacheTTL);
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(result);
        }
      }

      return result;

    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }

      // Only update state if this is still the latest call and component is mounted
      if (currentCall === callCountRef.current && isMountedRef.current) {
        const errorInfo = handleApiError(err, {
          ...context,
          apiCall: apiCall.name || 'unknown',
        });

        setError(errorInfo);
        setLoading(false);
        setIsFetching(false);

        // Call error callback
        if (onError) {
          onError(errorInfo);
        }
      }

      throw err;
    }
  }, [apiCall, cache, cacheTTL, generatedCacheKey, data, onSuccess, onError, context]);

  /**
   * Refetch function (forces fresh data from API)
   */
  const refetch = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  /**
   * Mutate function (optimistically update data without API call)
   * Useful for immediate UI updates before server confirmation
   */
  const mutate = useCallback((newData, shouldRevalidate = false) => {
    setData(newData);

    // Update cache
    if (cache) {
      apiCache.set(generatedCacheKey(), newData, cacheTTL);
    }

    // Optionally revalidate with server
    if (shouldRevalidate) {
      refetch();
    }
  }, [cache, cacheTTL, generatedCacheKey, refetch]);

  /**
   * Invalidate cache for this query
   */
  const invalidateCache = useCallback(() => {
    apiCache.invalidate(generatedCacheKey());
  }, [generatedCacheKey]);

  // Auto-fetch on mount and dependency changes
  useEffect(() => {
    if (enabled) {
      fetchData();
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track component mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    isFetching,
    refetch,
    mutate,
    invalidateCache,
  };
};

export default useApi;
